/* ============================================================
   送信前に画像を縮小する（買取査定フォーム専用）

   なぜ必要か
     査定写真は 5〜10枚 × 3〜8MB＝30〜80MB になりうる。
     サーバー（heteml）の upload_max_filesize は 100M で足りるが、
     **通知メールの添付として送ると受信側の上限（一般に10〜25MB）で弾かれる。**
     長辺1600px・JPEG品質0.85 なら1枚200〜400KBに収まり、10枚でも2〜4MB。

   方針
     - 選択された時点で縮小し、DataTransfer で input.files を差し替える。
       こうすると通常の multipart 送信のまま通せる（fetch/XHRにしなくてよい）
     - 使えない環境では**何もしない**。原本がそのまま送られるだけで、機能は壊れない
     - 縮小中は送信ボタンを止める。押せてしまうと原本が送られる

   踏みやすい罠
     - createImageBitmap に imageOrientation:"from-image" を渡さないと
       EXIFの回転情報が落ちて横倒しになる
     - 古いiOS Safariは DataTransfer が使えない → 差し替えられないので何もしない
     - HEICはcanvasでデコードできないことがある → 例外を握って原本を返す
     - EXIFが落ちるのは**この用途ではメリット**。写真から自宅の位置情報が漏れない
   ============================================================ */
(function () {
  "use strict";

  var MAX_EDGE = 1600;      // 長辺の上限（px）
  var QUALITY = 0.85;       // JPEGの品質
  var MAX_FILES = 5;        // 査定に必要な枚数。1枚200〜400KBなので5枚でも合計2MB以下
  var MAX_BYTES = 5242880;  // 1枚5MB。**縮小したあとの**サイズで見る（下記）

  var input = document.getElementById("lpf-photos");
  if (!input) return;

  var form = input.form;
  if (!form) return;

  /* 差し替えができない環境では動かさない。
     中途半端に縮小しても input.files に戻せず、原本が送られるだけになるため */
  var canReplace = false;
  try {
    canReplace = typeof DataTransfer === "function" && !!new DataTransfer().items;
  } catch (e) {
    canReplace = false;
  }
  if (!canReplace) return;
  if (typeof createImageBitmap !== "function" || typeof Promise !== "function") return;

  /* 状態表示。読み上げにも届くよう aria-live を付ける */
  var status = document.createElement("p");
  status.className = "lp-field__notice";
  status.setAttribute("aria-live", "polite");
  status.hidden = true;
  input.parentNode.insertBefore(status, input.nextSibling);

  var submitBtn = form.querySelector('button[type="submit"]');
  var busy = false;
  var replacing = false;   // 差し替え中の change を無視するための印

  function setBusy(on, message, isAlert) {
    busy = on;
    if (submitBtn) submitBtn.disabled = on;
    if (message) {
      status.textContent = message;
      status.className = "lp-field__notice" + (isAlert ? " lp-field__notice--alert" : "");
      status.hidden = false;
    }
  }

  /* 戻り値は { file, decoded }。
     decoded=false は **このブラウザで中身を読めなかった**もの。
     iPhoneのHEICをChrome/Firefoxで選ぶとここに来る。
     以前は原本をそのまま通していたため、5MB未満だと
     「調整しました」と出たうえで**送信してから弾かれていた。** */
  function resizeOne(file) {
    if (!file.type || file.type.indexOf("image/") !== 0) {
      return Promise.resolve({ file: file, decoded: false });
    }
    return createImageBitmap(file, { imageOrientation: "from-image" })
      .then(function (bitmap) {
        var w = bitmap.width;
        var h = bitmap.height;
        var scale = Math.min(1, MAX_EDGE / Math.max(w, h));
        var cw = Math.max(1, Math.round(w * scale));
        var ch = Math.max(1, Math.round(h * scale));

        var canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        var ctx = canvas.getContext("2d");
        /* 透過PNGをJPEGにすると透明部分が黒くなる。白で埋めてから描く */
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(bitmap, 0, 0, cw, ch);
        if (bitmap.close) bitmap.close();

        return new Promise(function (resolve) {
          canvas.toBlob(function (blob) {
            /* 縮小しても小さくならなかった場合は原本のまま送る */
            if (!blob || blob.size >= file.size) {
              resolve({ file: file, decoded: true });   // 読めたが縮まなかった
              return;
            }
            var name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
            resolve({ file: new File([blob], name, { type: "image/jpeg" }), decoded: true });
          }, "image/jpeg", QUALITY);
        });
      })
      .catch(function () {
        return { file: file, decoded: false };   // HEIC等でデコードできなかった
      });
  }

  input.addEventListener("change", function () {
    if (replacing) return;

    var files = Array.prototype.slice.call(input.files || []);
    if (!files.length) {
      status.hidden = true;
      return;
    }

    if (files.length > MAX_FILES) {
      setBusy(false, "写真は" + MAX_FILES + "枚までです。選び直してください。", true);
      replacing = true;
      input.value = "";
      replacing = false;
      return;
    }

    var before = files.reduce(function (a, f) { return a + f.size; }, 0);
    setBusy(true, "写真を送信できるサイズに調整しています…");

    Promise.all(files.map(resizeOne)).then(function (resized) {
      /* 容量の判定は**縮小したあと**にかける。先に弾くと、縮めれば通る写真まで落ちる。
         ここに残るのは「縮小できなかった原本」＝HEIC等でデコードに失敗したもの */
      var ok = [];
      var tooBig = [];
      var unreadable = [];
      resized.forEach(function (r) {
        if (!r.decoded) { unreadable.push(r.file); return; }
        (r.file.size > MAX_BYTES ? tooBig : ok).push(r.file);
      });

      var dt = new DataTransfer();
      ok.forEach(function (f) { dt.items.add(f); });

      replacing = true;
      input.files = dt.files;
      replacing = false;

      var after = ok.reduce(function (a, f) { return a + f.size; }, 0);
      /* 1MB未満をMB表記にすると「0.0MB」になって縮んだことが伝わらない */
      var size = function (n) {
        return n >= 1048576 ? (n / 1048576).toFixed(1) + "MB" : Math.round(n / 1024) + "KB";
      };

      var msg = "";
      if (ok.length) {
        msg = ok.length + "枚を調整しました（" + size(before) + " → " + size(after) + "）。";
      }
      if (tooBig.length) {
        /* 何が起きたかだけでなく、次に何をすればよいかまで書く。
           ここに来るのは縮小できなかったファイルなので、利用者にできるのは選び直しだけ */
        msg += (msg ? " " : "")
          + tooBig.map(function (f) { return f.name; }).join("、")
          + " は1枚5MBを超えているため添付できませんでした。"
          + "5MB以内の写真を選び直してください。";
      }
      if (unreadable.length) {
        /* ここに来るのはほぼ iPhoneのHEIC を Chrome/Firefox で選んだ場合。
           Safari と iOS は読めるので、そちらを案内する */
        var heic = unreadable.some(function (f) {
          return /\.(heic|heif)$/i.test(f.name) || /heic|heif/i.test(f.type || "");
        });
        msg += (msg ? " " : "")
          + unreadable.map(function (f) { return f.name; }).join("、")
          + " はこのブラウザで読み取れませんでした。"
          + (heic
              ? "iPhoneの写真形式（HEIC）です。Safariでお試しいただくか、スクリーンショットを撮って送ってください。"
              : "JPEG／PNG／WebP のいずれかで保存し直してください。");
      }
      setBusy(false, msg, tooBig.length > 0 || unreadable.length > 0);
    }).catch(function () {
      /* 途中で失敗しても原本がそのまま残っているので送信はできる */
      setBusy(false, "調整できなかった写真があります。そのまま送信できます。", true);
    });
  });

  /* 調整中に送信されると原本が飛ぶ。念のため送信側でも止める */
  form.addEventListener("submit", function (e) {
    if (busy) {
      e.preventDefault();
      status.textContent = "写真を調整しています。完了までお待ちください。";
    }
  });
})();
